import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Button, Checkbox, message, Skeleton, Switch, Tooltip } from "antd"
import { useLayoutEffect, useState, useContext } from "react"
import { ShowMessagePage } from "../../config"
import { getOrders, setOrderShippingStatus } from "../../endpoints"

import { DownloadOutlined, EyeOutlined, FileTextOutlined, SmileOutlined } from "@ant-design/icons"
import Table, { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { useLocation, useNavigate } from "react-router"
import { Link } from "react-router-dom"
import { useTheme } from "styled-components"
import { ContainerFull } from "../../components/GeneralComponents"
import { OrderInfoInterface } from "../../types"
import { UserContext } from "../../context/UserContext"

const dateFormat = "YYYY-MM-DD"
// value={dayjs('2015-06-06', dateFormat)}

export const UserOrders = (): JSX.Element => {
    // const [selectedRow, setSelectedRow] = useState<number>(-1)
    const [loadingShipping, setLoadingShipping] = useState<Set<number>>(new Set())
    const [showCanceledOrders, setShowCanceledOrders] = useState<boolean>(false)
    const [hideGroupOrders, setHideGroupOrders] = useState<boolean>(false)
    const [page, setPage] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(25)
    const location = useLocation()
    const navigate = useNavigate()
    const userInSession = useContext(UserContext)
    const currentUser = userInSession?.user
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getorders", "user", hideGroupOrders, showCanceledOrders, page, pageSize],
        queryFn: () => getOrders({
            type: "user",
            hideGroupOrders,
            showCanceled: showCanceledOrders,
            page,
            pageSize,
        }),
        refetchOnMount: "always",
    })
    const theme = useTheme()
    // const [orders, setOrders] = useState<OrderInfoInterface[]>([])
    const queryClient = useQueryClient()

    const orders = data?.orders ?? []

    useLayoutEffect(() => {
        // console.log("repainting ...")
    }, [])

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }

    // Check if user has view_group_orders permission
    const canViewGroupOrders = currentUser?.view_group_orders === true
    const showingGroupOrders = canViewGroupOrders && !hideGroupOrders

    const columns: ColumnsType<OrderInfoInterface> = [
        {
            title: "Details",
            dataIndex: "id",
            align: "center",
            render: (value, record, index) => {
                return (
                    <Tooltip title="view order">
                        <EyeOutlined
                            style={{ color: "blue" }}
                            onClick={async () => {
                                if (location.pathname.startsWith("/myspace")) {
                                    navigate("/myspace/order/" + record.id)
                                } else if (location.pathname.startsWith("/admin")) {
                                    navigate("/admin/order/" + record.id)
                                } else {
                                    //this should not happen
                                    navigate("/myspace/order/" + record.id)
                                }
                            }}
                        />
                    </Tooltip>
                )
            },
        },
        // Add Customer column if user has view_group_orders permission
        ...(canViewGroupOrders ? [{
            title: "Customer",
            dataIndex: "user",
            align: "center" as const,
            render: (text: any, record: OrderInfoInterface, index: number) => {
                const user = record.user
                return <span>{user?.firstName} {user?.lastName}</span>
            },
        }] : []),
        
        {
            title: "Results",
            dataIndex: "download",
            align: "center",
            render: (text, record, index) => {
                const allowedStatuses = [
                    "completed",
                    "review-requested",
                    "rerun-completed",
                    "review-completed",
                ]
                const canDownload = allowedStatuses.includes(record.status)
                const url = `/api/download/orderresult/${record.id}?r=${Math.random()}`
                return canDownload ? (
                    <Link to={url} target="_blank" rel="noopener noreferrer">
                        <DownloadOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                    </Link>
                ) : (
                    <DownloadOutlined style={{ fontSize: 18, color: '#bbb', cursor: 'not-allowed', opacity: 0.5 }} />
                )
            },
        },
        {
            title: "Order Sheet",
            dataIndex: "orderSheet",
            align: "center",
            render: (text, record, index) => {
                const url = `/api/download/ordersheet/${record.id}?r=${Math.random()}`
                return (
                    <Link to={url} target="_blank" rel="noopener noreferrer">
                        <FileTextOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                    </Link>
                )
            },
        },
        {
            title: "Order ID",
            dataIndex: "id",
            align: "center",
            render: (text, record, index) => {
                const orderPath = location.pathname.startsWith("/myspace")
                    ? `/myspace/order/${record.id}`
                    : `/admin/order/${record.id}`
                return <Link to={orderPath}>{record.id}</Link>
            },
        },
        {
            title: "Order Name",
            dataIndex: "ordername",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.ordername}</span>
            },
        },

        {
            title: "Shipping",
            dataIndex: "id",
            align: "center",
            render: (text, record, index) => {
                const isLoading = loadingShipping.has(record.id!)
                return (
                    // <Spin spinning={isLoading} size="small">
                        <Switch
                            checked={record.shipped}
                            disabled={isLoading}
                            style={{ backgroundColor: record.shipped ? "green" : "#ff4d4d" }}
                            checkedChildren={record.shippinginfo?.method === "dropbox" ? "Dropped off" : (record.shippinginfo?.method  === "pickup" ? "Pick-up ready" : "Shipped")}
                            unCheckedChildren={<span>Not ready</span>}
                            onChange={async (value) => {
                                //set shipped field to true in the database
                                // console.log("shipped", value)
                                // console.log('set loading to true')
                                // Add orderId to loading set
                                setLoadingShipping(prev => new Set(prev).add(record.id!))
                                
                                try {
                                    const result = await setOrderShippingStatus({
                                        orderId: record.id!,
                                        shipped: value,
                                    })
                                    // console.log(result)
                                    if (result.code === 1000) {
                                        //invalidate getuserorders
                                        queryClient.invalidateQueries({ queryKey: ["getorders", "user"] })
                                    }
                                } finally {
                                    // Remove orderId from loading set
                                    // console.log('set loading to false')

                                    setLoadingShipping(prev => {
                                        const newSet = new Set(prev)
                                        newSet.delete(record.id!)
                                        return newSet
                                    })
                                }
                            }}
                        />
                    // </Spin>
                )
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.status}</span>
            },
        },
        {
            title: "Submission",
            dataIndex: "submission_type",
            align: "center",
            render: (text, record, index) => {
                const submissionType = record.submission_type || 'unknown'
                const displayText = {
                    'unknown': '',
                    'tubes': 'Tubes',
                    'plate-column-order': 'Plate (Col)',
                    'plate-row-order': 'Plate (Row)',
                    'plate-map': 'Plate (Map)'
                }[submissionType] || ''
                return <span>{displayText}</span>
            },
        },

        {
            title: "Service",
            dataIndex: "productname",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.productname}</span>
            },
        },
        {
            title: "# of Samples",
            dataIndex: "samples",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.samples.length}</span>
            },
        },
        // {
        //     title: "Concentrations",
        //     dataIndex: "concentration",
        //     align: "center",
        //     render: (text, record, index) => {
        //         return <span>{record.concentration + "ng/μl"}</span>
        //     },
        // },

        {
            title: "Price",
            dataIndex: "totalPrice",
            align: "center",
            render: (text, record, index) => {
                return (
                    <span>
                        {record.totalPrice?.unit === "USD"
                            ? "$" + record.totalPrice.price
                            : (record.totalPrice?.price ?? "") + (record.totalPrice?.unit ?? "")}
                    </span>
                )
            },
        },
        {
            title: "Paid",
            dataIndex: "paymentInfo",
            align: "center",
            render: (text, record, index) => {
                const isPaid = record.paymentInfo?.paid
                return (
                    <span style={{ 
                        color: isPaid ? '#52c41a' : '#ff4d4f',
                        fontWeight: 500
                    }}>
                        {isPaid ? 'Yes' : 'No'}
                    </span>
                )
            },
        },
        {
            title: "Invoice",
            dataIndex: "invoice",
            align: "center",
            render: (text, record, index) => {
                const hasInvoice = record.invoiceId
                
                // Group members' invoices need view_group_invoices, same as the server check
                const isOwnOrder = record.user?.id === currentUser?.id
                if (hasInvoice && !isOwnOrder && !currentUser?.view_group_invoices) {
                    return <span style={{ color: '#bbb' }}>#{record.invoiceId}</span>
                }

                // Show a muted placeholder if there's no invoice
                if (!hasInvoice) {
                    return (
                        <Tooltip title="Invoice not available">
                            <span style={{ color: '#bbb' }}>--</span>
                        </Tooltip>
                    )
                }

                const handleInvoiceDownload = async () => {
                    try {
                        const url = `/api/download/invoice/order/${record.id}?r=${Math.random()}`
                        window.open(url, '_blank')
                    } catch (error) {
                        console.error('Error downloading invoice:', error)
                        message.error('Failed to download invoice')
                    }
                }

                // Show clickable invoice number for orders with invoice
                return (
                    <Tooltip title="Download Invoice">
                        <span
                            style={{
                                color: '#1890ff',
                                cursor: 'pointer'
                            }}
                            onClick={handleInvoiceDownload}
                        >
                            #{record.invoiceId}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            title: "Notes",
            dataIndex: "Notes",
            align: "center",
            render: (text, record, index) => {
                const notes = record.specialNotes || ""
                const displayNotes =
                    notes.length > 15 ? notes.slice(0, 15) + "..." : notes || <span style={{ color: "#bbb" }}> </span>
                return <span>{displayNotes}</span>
            },
        },

        {
            title: "Date created",
            dataIndex: "ts",
            align: "center",
            render: (text, record, index) => {
                return <span>{dayjs(record.ts).format(dateFormat)}</span>
            },
        },
        
    ]
    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            {location.state?.successMessage && (
                <div
                    style={{
                        background: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        color: '#389e0d',
                        borderRadius: 8,
                        padding: '1em 1.5em',
                        margin: '1.5em auto',
                        maxWidth: 600,
                        fontSize: 18,
                        fontWeight: 500,
                        textAlign: 'center',
                        boxShadow: '0 2px 8px rgba(56,158,13,0.08)'
                    }}
                >
                    {location.state.successMessage}
                </div>
            )}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>{showingGroupOrders ? 'Group Orders' : 'My Orders'}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                        Showing {orders.length} of {data?.total || 0} orders
                    </span>
                    
                    <Checkbox
                        checked={showCanceledOrders}
                        onChange={(e) => {
                            setShowCanceledOrders(e.target.checked)
                            setPage(1)
                        }}
                    >
                        Show canceled orders
                    </Checkbox>
                    {canViewGroupOrders && (
                        <Checkbox
                            checked={hideGroupOrders}
                            onChange={(e) => {
                                setHideGroupOrders(e.target.checked)
                                setPage(1)
                            }}
                        >
                            Hide group orders
                        </Checkbox>
                    )}
                </div>
            </div>
            {orders.length > 0 ? (
                <Table
                    dataSource={orders.map((row) => {
                        return { ...row, key: row.id }
                    })}
                    // rowClassName={(record, index) =>
                    //     index === selectedRow ? "highlighted-table-row" : "regular-table-row"
                    // }
                    columns={columns}
                    pagination={{
                        position: ["bottomLeft"],
                        current: page,
                        pageSize,
                        total: data?.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["25", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                        onChange: (p, ps) => {
                            setPage(p)
                            setPageSize(ps)
                        },
                    }}
                    size="middle"
                    onRow={(record, index) => {
                        return {
                            onClick: () => {
                                //   if (index === selectedRow) {
                                //     setSelectedRow(-1)
                                //   } else {
                                //     setSelectedRow(index ?? -1)
                                //   }
                            },
                        }
                    }}
                />
            ) : (
                <div style={{ textAlign: "center", marginTop: 50 }}>
                    <h2 style={{ color: theme.primary }}>
                        {orders.length === 0 
                            ? "No orders found" 
                            : "No orders to display"
                        }
                    </h2>

                    <div style={{ color: "black" }}>
                        {orders.length === 0 
                            ? "Ready to get started?" 
                            : "All your orders are canceled. Check 'Show canceled orders' to see them."
                        }
                    </div>
                    <div style={{ marginTop: "3em" }}>
                        {" "}
                        <SmileOutlined
                            style={{
                                fontSize: 64,
                                color: "orange",
                                marginBottom: 16,
                                animation: "bounce 1.5s infinite",
                            }}
                        />
                    </div>

                    <Button
                        type="default"
                        size="large"
                        style={{ marginTop: 16 }}
                        onClick={() => {
                            navigate("/myspace/order")
                        }}
                    >
                        Place a new order
                    </Button>
                    <style>
                        {`
                        @keyframes bounce {
                            0%, 100% { transform: translateY(0);}
                            50% { transform: translateY(-10px);}
                        }
                        `}
                    </style>
                </div>
            )}
        </ContainerFull>
    )
}
